export interface PublicStatusIncident {
  id?: string
  title?: string
  message?: string
  detail?: string
  status?: string
  kind?: string
  scope?: string
  active?: boolean
  updatedAt?: string
}

export interface PublicStatusMaintenanceWindow {
  id?: string
  title?: string
  message?: string
  kind?: string
  state?: string
  startAt?: string
  endAt?: string
  scheduleLabel?: string
  source?: string
}

export interface PublicStatusUpstreamNotice {
  id?: string
  name?: string
  message?: string
  detail?: string
  status?: string
  kind?: string
  scope?: string
  active?: boolean
  updatedAt?: string
}

export interface PublicStatusProviderConnection {
  provider?: string
  label?: string
  healthy?: boolean
  message?: string
  detail?: string
  checkedAt?: string
}

export interface PublicStatusService {
  id?: string
  name?: string
  description?: string
  status?: string
  message?: string
  detail?: string
  updatedAt?: string
}

export interface PublicStatusServiceGroup {
  id?: string
  name?: string
  services?: PublicStatusService[]
}

export interface PublicStatusResponse {
  generatedAt?: string
  incidents?: PublicStatusIncident[]
  maintenanceWindows?: PublicStatusMaintenanceWindow[]
  upstreamNotices?: PublicStatusUpstreamNotice[]
  providerConnections?: PublicStatusProviderConnection[]
  serviceGroups?: PublicStatusServiceGroup[]
}

export interface PublicStatusBanner {
  tone: 'error' | 'info'
  message: string
  linkLabel: string
  linkHref: string
  countdownTargetAt?: string
  countdownPrefix?: string
}

export type PublicMaintenanceBanner = PublicStatusBanner

export const normalizePublicStatusResponse = (
  value: unknown,
): PublicStatusResponse | null => {
  if (!value || typeof value !== 'object') return null

  const candidate = value as { data?: unknown }
  if ('data' in candidate && candidate.data && typeof candidate.data === 'object') {
    return candidate.data as PublicStatusResponse
  }

  return value as PublicStatusResponse
}

const isActiveIssue = (value?: boolean) => value !== false

const parseDate = (value?: string) => {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return date
}

const isWithinNextHour = (value?: string, now = new Date()) => {
  const date = parseDate(value)
  if (!date) return false

  const diff = date.getTime() - now.getTime()
  return diff >= 0 && diff <= 60 * 60 * 1000
}

const hasActiveIncident = (response: PublicStatusResponse) => {
  if ((response.incidents ?? []).some((incident) => isActiveIssue(incident.active))) return true

  for (const group of response.serviceGroups ?? []) {
    for (const service of group.services ?? []) {
      const normalizedStatus = (service.status || '').toLowerCase()
      if (normalizedStatus === 'operational') continue
      if (normalizedStatus === 'unknown') continue
      if (normalizedStatus === 'planned' || normalizedStatus === 'upcoming') continue
      return true
    }
  }

  return false
}

export const buildPublicStatusBanner = (
  response: PublicStatusResponse | null | undefined,
): PublicStatusBanner | null => {
  if (!response || !hasActiveIncident(response)) return null

  return {
    tone: 'error',
    message: 'Es gibt derzeit eine Störung im System.',
    linkLabel: 'Statusseite',
    linkHref: 'https://status.mbcom.cloud',
  }
}

export const buildPublicMaintenanceBanner = (
  response: PublicStatusResponse | null | undefined,
  now = new Date(),
): PublicMaintenanceBanner | null => {
  if (!response) return null

  const activeWindows = (response.maintenanceWindows ?? []).filter((window) => {
    const state = (window.state || '').toLowerCase()
    if (state === 'active') return true

    const isScheduledState = state === 'upcoming' || state === 'planned'
    if (!isScheduledState) return false

    const startDate = parseDate(window.startAt)
    if (!startDate) return false

    return startDate.getTime() <= now.getTime()
  })

  if (activeWindows.length > 0) {
    return {
      tone: 'info',
      message: 'Es läuft derzeit eine Wartung.',
      linkLabel: 'Statusseite',
      linkHref: 'https://status.mbcom.cloud',
    }
  }

  const upcomingWindow = (response.maintenanceWindows ?? [])
    .filter((window) => {
      const state = (window.state || '').toLowerCase()
      return state === 'upcoming' || state === 'planned'
    })
    .filter((window) => isWithinNextHour(window.startAt, now))
    .sort((a, b) => {
      const aStart = parseDate(a.startAt)?.getTime() ?? Number.POSITIVE_INFINITY
      const bStart = parseDate(b.startAt)?.getTime() ?? Number.POSITIVE_INFINITY
      return aStart - bStart
    })[0]

  if (!upcomingWindow) return null

  return {
    tone: 'info',
    message: upcomingWindow.title || 'Geplante Wartung',
    countdownTargetAt: upcomingWindow.startAt,
    countdownPrefix: 'startet in',
    linkLabel: 'Statusseite',
    linkHref: 'https://status.mbcom.cloud',
  }
}
